import { useCallback, useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { DiscountType, FeatureFlagUseCase, RechargeDayType, ReleaseType } from '@ibk/shared-types';
import { formatCurrency } from '@ibk/shared-utils';
import { getUnformatedValue, isActiveStatus, shouldUpdateBeneficiaries, updateBeneficiariesDiscounts, updateBeneficiariesValues, } from '../../../utils/beneficiaryUtils';
const DEFAULT_CURRENCY_VALUE = 'R$ 0,00';
const DEFAULT_PERCENTAGE_VALUE = '0,00%';
export function useParametersForm({ voucher }) {
    if (!voucher?.voucherType) {
        throw new Error('useParametersForm: voucher must contain a voucherType');
    }
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isBeneficiaryDiscountEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_BENEFICIARY_DISCOUNT);
    const getInitialGlobalValue = () => {
        if (voucher?.releaseType === ReleaseType.DAILY && voucher.amountDaily) {
            return formatCurrency(voucher.amountDaily);
        }
        if (voucher?.releaseType === ReleaseType.MONTHLY && voucher.baseValue) {
            return formatCurrency(voucher.baseValue);
        }
        return DEFAULT_CURRENCY_VALUE;
    };
    const [step2Data, setStep2Data] = useState({
        releaseType: voucher?.releaseType || ReleaseType.MONTHLY,
        globalValue: getInitialGlobalValue(),
        beneficiaries: [],
    });
    const applyMask = useCallback((value, type) => {
        const numbers = value.replace(/\D/g, '');
        if (type === DiscountType.TOTAL) {
            if (numbers === '')
                return DEFAULT_CURRENCY_VALUE;
            const formattedNumber = numbers.padStart(3, '0');
            const reais = formattedNumber.slice(0, -2);
            const centavos = formattedNumber.slice(-2);
            const formattedReais = parseInt(reais || '0').toLocaleString('pt-BR');
            return `R$ ${formattedReais},${centavos}`;
        }
        else {
            if (numbers === '')
                return DEFAULT_PERCENTAGE_VALUE;
            const digits = numbers.padStart(3, '0');
            const inteiros = digits.slice(0, -2);
            const decimais = digits.slice(-2);
            return `${parseInt(inteiros)},${decimais}%`;
        }
    }, []);
    const [formData, setFormData] = useState({
        rechargeDayType: voucher.rechargeDayType,
        rechargeDay: voucher.rechargeDay,
        payrollDeduction: voucher.discountType !== DiscountType.NO_DISCOUNT,
        discountType: voucher.discountType,
        discountValue: voucher.discountType !== DiscountType.NO_DISCOUNT
            ? applyMask((voucher.discountValue?.toFixed(2) ?? '').toString(), voucher.discountType)
            : '',
        isIndividualDiscount: voucher.isIndividualDiscount ?? false,
    });
    const handleRechargeDayTypeChange = useCallback((value) => {
        setFormData(prev => ({
            ...prev,
            rechargeDayType: value,
            rechargeDay: value === RechargeDayType.FIXED_DAY ? 1 : null,
        }));
    }, []);
    const handleRechargeDayChange = useCallback((value) => {
        if (value.trim() === '') {
            setFormData(prev => ({ ...prev, rechargeDay: null }));
            return;
        }
        const digitsOnly = value.replace(/\D/g, '').slice(0, 2);
        const numeric = parseInt(digitsOnly, 10);
        if (Number.isNaN(numeric)) {
            setFormData(prev => ({ ...prev, rechargeDay: null }));
            return;
        }
        const clamped = Math.max(1, Math.min(99, numeric));
        setFormData(prev => ({
            ...prev,
            rechargeDay: clamped,
        }));
    }, []);
    const calculateShouldApplyDiscountValue = useCallback((payrollDeduction) => {
        return isBeneficiaryDiscountEnabled && payrollDeduction;
    }, [isBeneficiaryDiscountEnabled]);
    const updateSelectedBeneficiariesFromBeneficiaries = useCallback((beneficiaries, currentSelected) => {
        if (!currentSelected) {
            return currentSelected;
        }
        const currentSelectedMap = new Map(currentSelected?.map(selected => [selected.beneficiaryId, selected]) ?? []);
        const beneficiariesMap = new Map(beneficiaries.map(b => [b.id, b]));
        const updatedSelected = [];
        currentSelectedMap.forEach((selected, beneficiaryId) => {
            const beneficiary = beneficiariesMap.get(beneficiaryId);
            if (beneficiary && isActiveStatus(beneficiary.statusInProduct)) {
                updatedSelected.push({
                    ...selected,
                    value: beneficiary.productValue,
                    daysQuantity: beneficiary.productDaysQuantity ?? selected.daysQuantity ?? 0,
                    discountValue: beneficiary.productDiscountValue ?? selected.discountValue ?? 0,
                });
            }
            else if (!beneficiary) {
                updatedSelected.push(selected);
            }
        });
        beneficiaries.forEach(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct) && !currentSelectedMap.has(beneficiary.id)) {
                updatedSelected.push({
                    beneficiaryId: beneficiary.id,
                    value: beneficiary.productValue,
                    daysQuantity: beneficiary.productDaysQuantity ?? 0,
                    discountValue: beneficiary.productDiscountValue ?? 0,
                });
            }
        });
        return updatedSelected;
    }, []);
    const handlePayrollDeductionChange = useCallback((value) => {
        setFormData(prev => {
            let deductionType = DiscountType.NO_DISCOUNT;
            if (value) {
                deductionType = prev.discountType === DiscountType.NO_DISCOUNT ? DiscountType.TOTAL : prev.discountType;
            }
            let discountValue = '';
            if (deductionType === DiscountType.TOTAL) {
                discountValue = DEFAULT_CURRENCY_VALUE;
            }
            else if (deductionType === DiscountType.PERCENTAGE) {
                discountValue = DEFAULT_PERCENTAGE_VALUE;
            }
            const updatedFormData = {
                ...prev,
                payrollDeduction: value,
                discountType: deductionType,
                discountValue,
                isIndividualDiscount: value ? prev.isIndividualDiscount : false,
            };
            setStep2Data(step2Prev => {
                const updatedBeneficiaries = updateBeneficiariesDiscounts(step2Prev.beneficiaries, updatedFormData.discountValue, isBeneficiaryDiscountEnabled, updatedFormData.payrollDeduction ?? false);
                const updatedSelectedBeneficiaries = updateSelectedBeneficiariesFromBeneficiaries(updatedBeneficiaries, step2Prev.selectedBeneficiaries);
                return {
                    ...step2Prev,
                    beneficiaries: updatedBeneficiaries,
                    selectedBeneficiaries: updatedSelectedBeneficiaries ?? step2Prev.selectedBeneficiaries,
                    shouldApplyDiscountValue: calculateShouldApplyDiscountValue(updatedFormData.payrollDeduction ?? false),
                };
            });
            return updatedFormData;
        });
    }, [updateSelectedBeneficiariesFromBeneficiaries, calculateShouldApplyDiscountValue, isBeneficiaryDiscountEnabled]);
    const handleDiscountTypeChange = useCallback((value) => {
        const newType = value;
        setFormData(prev => {
            const updatedFormData = {
                ...prev,
                discountType: newType,
            };
            setStep2Data(step2Prev => {
                const updatedBeneficiaries = updateBeneficiariesDiscounts(step2Prev.beneficiaries, updatedFormData.discountValue, isBeneficiaryDiscountEnabled, updatedFormData.payrollDeduction ?? false);
                const updatedSelectedBeneficiaries = updateSelectedBeneficiariesFromBeneficiaries(updatedBeneficiaries, step2Prev.selectedBeneficiaries);
                return {
                    ...step2Prev,
                    beneficiaries: updatedBeneficiaries,
                    selectedBeneficiaries: updatedSelectedBeneficiaries ?? step2Prev.selectedBeneficiaries,
                    shouldApplyDiscountValue: calculateShouldApplyDiscountValue(updatedFormData.payrollDeduction ?? false),
                };
            });
            return updatedFormData;
        });
    }, [updateSelectedBeneficiariesFromBeneficiaries, calculateShouldApplyDiscountValue, isBeneficiaryDiscountEnabled]);
    const handleDiscountValueChange = useCallback((value) => {
        setFormData(prev => {
            const updatedFormData = {
                ...prev,
                discountValue: value,
            };
            setStep2Data(step2Prev => {
                const updatedBeneficiaries = updateBeneficiariesDiscounts(step2Prev.beneficiaries, value, isBeneficiaryDiscountEnabled, updatedFormData.payrollDeduction ?? false);
                const updatedSelectedBeneficiaries = updateSelectedBeneficiariesFromBeneficiaries(updatedBeneficiaries, step2Prev.selectedBeneficiaries);
                return {
                    ...step2Prev,
                    beneficiaries: updatedBeneficiaries,
                    selectedBeneficiaries: updatedSelectedBeneficiaries ?? step2Prev.selectedBeneficiaries,
                    shouldApplyDiscountValue: calculateShouldApplyDiscountValue(updatedFormData.payrollDeduction ?? false),
                };
            });
            return updatedFormData;
        });
    }, [updateSelectedBeneficiariesFromBeneficiaries, calculateShouldApplyDiscountValue, isBeneficiaryDiscountEnabled]);
    const handleIsIndividualDiscountChange = useCallback((value) => {
        setFormData(prev => {
            const updatedFormData = {
                ...prev,
                isIndividualDiscount: value,
            };
            setStep2Data(step2Prev => {
                const updatedBeneficiaries = updateBeneficiariesDiscounts(step2Prev.beneficiaries, updatedFormData.discountValue, isBeneficiaryDiscountEnabled, value);
                const updatedSelectedBeneficiaries = updateSelectedBeneficiariesFromBeneficiaries(updatedBeneficiaries, step2Prev.selectedBeneficiaries);
                return {
                    ...step2Prev,
                    beneficiaries: updatedBeneficiaries,
                    selectedBeneficiaries: updatedSelectedBeneficiaries ?? step2Prev.selectedBeneficiaries,
                    shouldApplyDiscountValue: calculateShouldApplyDiscountValue(value),
                };
            });
            return updatedFormData;
        });
    }, [updateSelectedBeneficiariesFromBeneficiaries, calculateShouldApplyDiscountValue, isBeneficiaryDiscountEnabled]);
    const handleReleaseTypeChange = useCallback((value) => {
        const newReleaseType = value;
        let newGlobalValue = DEFAULT_CURRENCY_VALUE;
        if (newReleaseType === ReleaseType.MONTHLY) {
            newGlobalValue = voucher?.baseValue ? formatCurrency(voucher.baseValue) : DEFAULT_CURRENCY_VALUE;
        }
        else {
            newGlobalValue = voucher?.amountDaily ? formatCurrency(voucher?.amountDaily) : DEFAULT_CURRENCY_VALUE;
        }
        setStep2Data(prev => {
            const updatedData = {
                ...prev,
                releaseType: newReleaseType,
                globalValue: newGlobalValue,
                shouldApplyGlobalValue: true,
            };
            if (shouldUpdateBeneficiaries(newReleaseType, newGlobalValue, prev.beneficiaries, DEFAULT_CURRENCY_VALUE)) {
                const globalNumericValue = getUnformatedValue(newGlobalValue);
                updatedData.beneficiaries = updateBeneficiariesValues(prev.beneficiaries, globalNumericValue, newReleaseType);
            }
            return updatedData;
        });
    }, [voucher]);
    const handleGlobalValueChange = useCallback((value) => {
        const maskedValue = applyMask(value, DiscountType.TOTAL);
        setStep2Data(prev => {
            const updatedData = {
                ...prev,
                globalValue: maskedValue,
                shouldApplyGlobalValue: true,
            };
            if (shouldUpdateBeneficiaries(prev.releaseType, maskedValue, prev.beneficiaries, DEFAULT_CURRENCY_VALUE)) {
                const globalNumericValue = getUnformatedValue(maskedValue);
                const updatedBeneficiaries = updateBeneficiariesValues(prev.beneficiaries, globalNumericValue, prev.releaseType);
                updatedData.beneficiaries = updatedBeneficiaries;
                const updatedSelectedBeneficiaries = updateSelectedBeneficiariesFromBeneficiaries(updatedBeneficiaries, prev.selectedBeneficiaries);
                updatedData.selectedBeneficiaries = updatedSelectedBeneficiaries ?? prev.selectedBeneficiaries;
            }
            return updatedData;
        });
    }, [applyMask, updateSelectedBeneficiariesFromBeneficiaries]);
    const handleUpdateBeneficiaries = useCallback((newBeneficiaries, selectedBeneficiaries, appliedFilters, shouldResetFlags = false) => {
        setStep2Data(prev => {
            if (JSON.stringify(prev.beneficiaries) === JSON.stringify(newBeneficiaries)) {
                if (selectedBeneficiaries || appliedFilters !== undefined || shouldResetFlags) {
                    return {
                        ...prev,
                        selectedBeneficiaries: selectedBeneficiaries ?? prev.selectedBeneficiaries,
                        appliedFilters: appliedFilters !== undefined ? appliedFilters : prev.appliedFilters,
                        shouldApplyGlobalValue: shouldResetFlags ? false : prev.shouldApplyGlobalValue,
                        shouldApplyDiscountValue: shouldResetFlags ? false : prev.shouldApplyDiscountValue,
                    };
                }
                return prev;
            }
            return {
                ...prev,
                beneficiaries: newBeneficiaries,
                selectedBeneficiaries: selectedBeneficiaries ?? prev.selectedBeneficiaries,
                appliedFilters: appliedFilters !== undefined ? appliedFilters : prev.appliedFilters,
                shouldApplyGlobalValue: shouldResetFlags ? false : prev.shouldApplyGlobalValue,
                shouldApplyDiscountValue: shouldResetFlags ? false : prev.shouldApplyDiscountValue,
            };
        });
    }, []);
    return {
        formData,
        step2Data,
        handlers: {
            handleRechargeDayTypeChange,
            handleRechargeDayChange,
            handlePayrollDeductionChange,
            handleDiscountTypeChange,
            handleDiscountValueChange,
            handleIsIndividualDiscountChange,
            handleReleaseTypeChange,
            handleGlobalValueChange,
            handleUpdateBeneficiaries,
        },
    };
}
